// Anniversary display labels (W3.1 §5.6 / §5.7).
//
// Pure label / formatter helpers used by HeroSection segment 1 (交往天數) +
// EventCard aria-labels + edit page section titles. No DOM access.

import { differenceInCalendarDays, differenceInYears, parseISO } from "date-fns";

/**
 * Chinese-numeral formatter for 交往第 N 週年 (1..∞).
 * Per W3.1 spec the label uses CJK numerals up to 10, then arabic.
 */
const CJK_NUMERALS: readonly string[] = Object.freeze([
  "零",
  "一",
  "二",
  "三",
  "四",
  "五",
  "六",
  "七",
  "八",
  "九",
  "十",
]);

export function zhOrdinal(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n <= 10) return CJK_NUMERALS[n] ?? String(n);
  if (n < 20) return `十${CJK_NUMERALS[n - 10] ?? ""}`;
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return `${CJK_NUMERALS[tens] ?? ""}十${ones === 0 ? "" : (CJK_NUMERALS[ones] ?? "")}`;
}

/** 「交往第 N 天」(用於 hero 大字 + 副標籤). */
export function daysTogetherLabel(days: number): string {
  if (!Number.isFinite(days)) return "0 天";
  return `${days.toLocaleString("zh-Hant")} 天`;
}

/** 「交往 N 週年」標籤 (從 anchor 日期算). */
export function anniversaryNthLabel(
  anchorIso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!anchorIso) return "";
  const d = parseISO(anchorIso);
  if (Number.isNaN(d.getTime())) return "";
  const days = differenceInCalendarDays(now, d);
  if (days < 0) return ""; // future anchor
  const years = differenceInYears(now, d);
  if (years < 1) return daysTogetherLabel(days);
  return `交往 ${zhOrdinal(years)} 週年`;
}

/**
 * Compose the segment-1 meta string.
 *
 * **Order matters, and it is load-bearing.** The line is `truncate`, so whatever
 * falls past its width is gone. Measured at 360px, this line needs 258 px and
 * has 188: **27% of it is cut**, past the 20% at which an ellipsis stops being
 * an abbreviation and starts hiding the part that carried the meaning.
 *
 * Two things came out of that measurement.
 *
 * **The anchor date is gone.** It used to lead the line — `從 2024-01-01 · 二 週年
 * · 還有 86 天到 三 週年` — and reordering only ever changed *which* part got cut,
 * never how much, because all three orders are the same 258 px. The date is the
 * least actionable of the three facts on a card that already opens with 「交往紀念」
 * over the day count derived from it, and it was the sole reason the line did not
 * fit. Dropping it is the only change that actually shortens the line.
 *
 * **What is left is the abbreviation band.** `二 週年 · 還有 86 天到 三 週年` is ~170 px
 * against 148 px at 320 — 13%, inside the band where the ellipsis is doing what
 * ellipses are for, and the cut lands inside the countdown instead of after a
 * `·` that now points at nothing.
 *
 * The old order put the date last and kept it, which left the rendered line
 * ending in a dangling separator and the audit over threshold at 320 / 360 / 375.
 */
export function anchorMetaLabel(
  anchorIso: string | null | undefined,
  now: Date = new Date(),
): string {
  if (!anchorIso) return "";
  const anchor = parseISO(anchorIso);
  if (Number.isNaN(anchor.getTime())) return "";
  const days = differenceInCalendarDays(now, anchor);
  if (days < 0) return "";
  const years = differenceInYears(now, anchor);
  const yearsLabel = years < 1 ? "剛在一起" : `${zhOrdinal(years)} 週年`;
  const nextAnniversary = new Date(anchor);
  nextAnniversary.setFullYear(nextAnniversary.getFullYear() + years + 1);
  const daysToNext = differenceInCalendarDays(nextAnniversary, now);
  const nextN = years + 1;
  return `${yearsLabel} · 還有 ${daysToNext} 天到 ${zhOrdinal(nextN)} 週年`;
}
